import { ThemedText } from '@/components/themed-text';
import { Card } from '@/components/ui/card';
import { cn } from '@/utils/cn';
import { Ionicons } from '@expo/vector-icons';
import React from 'react';
import { Platform, View } from 'react-native';
import { useThemedHex } from '@/hooks/use-theme-colors';

type MetricCardProps = {
  icon: React.ComponentProps<typeof Ionicons>['name'];
  value: string;
  label?: string; // Deprecated: use title instead
  title?: string; // New prop name matching example 10.3
  percentageChange?: number | null; // Deprecated: use change instead
  change?: number | null; // New prop name matching example 10.3
  trend?: 'up' | 'down' | 'neutral';
  compact?: boolean;
};

export function MetricCard({
  icon,
  value,
  label,
  title,
  percentageChange,
  change,
  trend,
  compact,
}: MetricCardProps) {
  const th = useThemedHex();
  // Support both old and new prop names for backward compatibility
  const displayTitle = title || label || '';
  const displayChange = change !== undefined ? change : percentageChange;

  const isPositive = displayChange !== undefined && displayChange !== null && displayChange >= 0;

  const finalTrend =
    trend ||
    (displayChange !== undefined && displayChange !== null
      ? isPositive
        ? 'up'
        : 'down'
      : 'neutral');

  const trendClasses =
    finalTrend === 'up'
      ? { chip: 'bg-success-50', text: 'text-success-600', icon: '#16A34A' }
      : finalTrend === 'down'
        ? { chip: 'bg-error-50', text: 'text-error-600', icon: '#DC2626' }
        : { chip: 'bg-neutral-100', text: 'text-neutral-500', icon: th('#737373') };

  const trendIcon =
    finalTrend === 'up' ? 'trending-up' : finalTrend === 'down' ? 'trending-down' : 'remove';

  return (
    // h-full só na web (grid dá altura definida ao pai); no nativo, dentro do
    // ScrollView não há altura de referência e o card estica sem limite.
    <Card
      className={cn(
        'min-h-28 justify-between',
        Platform.OS === 'web' && 'h-full',
        compact ? 'p-3' : 'p-4',
      )}
    >
      <View className="flex-row items-center justify-between">
        <View
          className={cn(
            'items-center justify-center rounded-xl bg-primary-50 dark:bg-primary',
            compact ? 'h-8 w-8' : 'h-9 w-9',
          )}
          style={{ borderCurve: 'continuous' }}
        >
          <Ionicons className="text-primary dark:text-white" name={icon} size={compact ? 15 : 18} />
        </View>
        {displayChange !== undefined && displayChange !== null && (
          <View
            className={cn('flex-row items-center gap-1 rounded-full px-2 py-0.5', trendClasses.chip)}
          >
            <Ionicons name={trendIcon as any} size={12} color={trendClasses.icon} />
            <ThemedText className={cn('text-xs font-semibold', trendClasses.text)}>
              {Math.abs(displayChange)}%
            </ThemedText>
          </View>
        )}
      </View>
      <View className={cn('gap-0.5', compact ? 'mt-2' : 'mt-3')}>
        <ThemedText
          className={cn('font-bold text-foreground', compact ? 'text-lg' : 'text-2xl')}
          style={{ fontVariant: ['tabular-nums'] }}
          numberOfLines={1}
          adjustsFontSizeToFit
        >
          {value}
        </ThemedText>
        <ThemedText className="text-xs font-medium text-neutral-500" numberOfLines={1}>
          {displayTitle}
        </ThemedText>
      </View>
    </Card>
  );
}
